/**
 * Students per lecturer FTE, by campus — and growth against student numbers.
 *
 * A payroll-month measure from two Finance sources (the student file and the payroll
 * workbook). It moves with neither control on this page, so the panel says so, and it is
 * dated by its own payroll month: never by the turnover window, and never by the frame it
 * rides on.
 *
 * A campus paying fewer than five lecturer FTE arrives withheld — its FTE and ratio null,
 * the row kept — and is never worked back from the total, which still includes it. Where
 * the finalised board pack printed a different figure, the panel bridges to it rather than
 * restating either.
 */
import React, { useMemo, type ReactNode } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { readCampusNames } from './IcaGovernancePanel';
import { LegendSwatch, PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  ACCENT,
  AXIS,
  GRID,
  INK,
  Def,
  fmtDay,
  fmtMonth,
  fmtNum,
  fmtOne,
  fmtPct,
  panelDateLabel,
  readExtras,
  type DefLookup,
  type PackBridge,
  type RatioDetail,
  type WorkforcePanels,
  type WorkforceRatioRow,
} from './apiContract';

interface RatioPanelProps {
  panels: WorkforcePanels | null;
  loading: boolean;
  def: DefLookup;
}

/** Employees lead, in the house ink; students and role titles beside them. */
const EMPLOYEES_COLOR = INK;
const STUDENTS_COLOR = ACCENT;
const TITLES_COLOR = '#0B7F72';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** "2026-07-31" -> "31 July". */
function dayAndMonth(iso?: string | null): string | null {
  const m = /^\d{4}-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${Number(m[2])} ${MONTHS[Number(m[1]) - 1]}` : null;
}

/** "2026-07" -> "July". */
function monthName(ym?: string | null): string | null {
  const m = /^\d{4}-(\d{2})/.exec(ym ?? '');
  return m ? MONTHS[Number(m[1]) - 1] ?? null : null;
}

const SPAN_WORDS = ['', 'one', 'two', 'three', 'four', 'five'];

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** The finalised board pack's entry for one of its tabs, where the block carries one. */
function finalisedBridge(list: PackBridge[] | undefined, tab: string): PackBridge | null {
  return (
    (Array.isArray(list) ? list : []).find(
      (b) => b?.tab === tab && typeof b.pack === 'string' && b.pack.startsWith('Finalised'),
    ) ?? null
  );
}

// =============================================================================
// DEFINITIONS — server definitions win; these fill the keys it does not publish.
// =============================================================================

const defBody = (label: string, body: string, caveat?: string): ReactNode => (
  <div className="space-y-1.5">
    <p className="font-semibold text-white">{label}</p>
    <p>{body}</p>
    {caveat ? (
      <ul className="list-disc space-y-0.5 pl-3.5 text-[11px] text-amber-200/90">
        <li>{caveat}</li>
      </ul>
    ) : null}
  </div>
);

const LOCAL_DEFS: Record<string, ReactNode> = {
  ratio_withheld: defBody(
    'Withheld',
    'Withheld to protect small groups: the campus pays fewer than five lecturer full-time equivalents, so its lecturer FTE and ratio are not shown. Its students and lecturers stay in the total.',
  ),
  ratio_no_students: defBody(
    'No student figure',
    'No invoiced student figure is available for this campus in the payroll month. It is not a zero.',
  ),
  ratio_not_available: defBody(
    'Not available for this date',
    'This figure is not available for the payroll month shown. It is not a zero.',
  ),
  ratio_growth: defBody(
    'Growth against student numbers',
    'Invoiced students, employees in post and distinct role titles on the same date each year, with the first year indexed to 100.',
    'Employees in post are permanent and fixed-term staff. Independent contractors and casual workers are not included.',
  ),
  growth_employees: defBody(
    'Employees in post',
    'People whose most permanent position is permanent or fixed-term, on that date.',
  ),
  growth_students: defBody(
    'Invoiced students',
    'Students invoiced in the payroll month of that year, new and returning.',
  ),
  growth_role_titles: defBody('Role titles', 'Distinct role titles on the establishment on that date.'),
  growth_ex_online: defBody(
    'Excluding the online campus',
    'The online campus removed from both the students and the employees, for a like-for-like view across years. An employee counts at the campus of their first employee position.',
  ),
};

/** A dash that says what it stands for, to a screen reader and in its tooltip. */
const Dash: React.FC<{ def: DefLookup; k: string; text: string }> = ({ def, k, text }) => (
  <span
    className="inline-flex items-center justify-end text-gray-400"
    title={text.charAt(0).toUpperCase() + text.slice(1)}
  >
    <span aria-hidden="true">—</span>
    <span className="sr-only">{text}</span>
    <Def def={def} k={k} />
  </span>
);

// =============================================================================
// GROWTH AGAINST STUDENT NUMBERS — `extras.ratio_detail.growth`
// =============================================================================

type Growth = NonNullable<RatioDetail['growth']>;

interface YearRow {
  key: string;
  asAt: string;
  employees: number | undefined;
  students: number | undefined;
  perEmployee: number | undefined;
}

const YearTable: React.FC<{ rows: YearRow[]; def: DefLookup }> = ({ rows, def }) => {
  const cell = (v: number | undefined, fmt: (n: number) => string) =>
    isNum(v) ? fmt(v) : <Dash def={def} k="ratio_not_available" text="not available for this date" />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="border-b text-gray-500">
          <tr>
            <th className="px-2 py-1.5 text-left font-medium">As at</th>
            <th className="px-2 py-1.5 text-right font-medium">
              <span className="inline-flex items-center justify-end">
                Employees in post
                <Def def={def} k="growth_employees" />
              </span>
            </th>
            <th className="px-2 py-1.5 text-right font-medium">
              <span className="inline-flex items-center justify-end">
                Invoiced students
                <Def def={def} k="growth_students" />
              </span>
            </th>
            <th className="px-2 py-1.5 text-right font-medium">Students per employee</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b last:border-0 hover:bg-gray-50">
              <td className="px-2 py-1.5 text-gray-700">{fmtDay(r.asAt)}</td>
              <td className="px-2 py-1.5 text-right font-medium tabular-nums text-gray-800">
                {cell(r.employees, fmtNum)}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">{cell(r.students, fmtNum)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">{cell(r.perEmployee, fmtOne)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const GrowthSection: React.FC<{
  growth: Growth;
  bridge: PackBridge | null;
  payrollMonth: string | undefined;
  def: DefLookup;
}> = ({ growth, bridge, payrollMonth, def }) => {
  const years = Array.isArray(growth.years) ? growth.years.filter(Boolean) : [];
  if (!years.length) return null;
  const first = years[0];
  const last = years[years.length - 1];
  const on = dayAndMonth(growth.as_at) ?? dayAndMonth(last.as_at) ?? 'the same date';
  const exOnline = years.filter((y) => isNum(y.students_per_employee_ex_online));

  const asAtByLabel = new Map(years.map((y) => [y.label, y.as_at]));
  const chart = (Array.isArray(growth.index) ? growth.index : []).filter(Boolean).map((p) => {
    const asAt = asAtByLabel.get(p.label);
    return {
      label: asAt ? fmtMonth(asAt) : p.label,
      employees: isNum(p.employees) ? p.employees : null,
      students: isNum(p.students) ? p.students : null,
      role_titles: isNum(p.role_titles) ? p.role_titles : null,
    };
  });

  const pct: Partial<Growth['growth_pct']> = growth.growth_pct ?? {};
  const moved = (label: string, v: unknown) =>
    isNum(v) ? `${label} ${v >= 0 ? 'up' : 'down'} ${fmtPct(Math.abs(v))}` : null;
  const movements = [
    moved('employees in post', pct.employees),
    moved('invoiced students', pct.students),
    moved('role titles', pct.role_titles),
  ].filter((s): s is string => !!s);

  const onlineRemoved = exOnline.length > 0;
  const firstHadNoOnlineStudents = onlineRemoved && first.online_students === 0;
  const retiredCaveat = onlineRemoved && years.some((y) => isNum(y.employees_without_campus) && y.employees_without_campus > 0);

  const span = years.length - 1;
  const spanWords = SPAN_WORDS[span] ? `${SPAN_WORDS[span]}-year` : `${span}-year`;
  const bridgePublished = bridge?.published?.growth_employees_pct;
  const bridgeNow = bridge?.now?.growth_employees_pct;
  const month = monthName(payrollMonth);

  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <div className="flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          Growth against student numbers
          <Def def={def} k="ratio_growth" />
        </div>
        <div className="text-[11px] text-gray-500">{on} of each year</div>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <YearTable
            def={def}
            rows={years.map((y) => ({
              key: y.label,
              asAt: y.as_at,
              employees: y.employees,
              students: y.students,
              perEmployee: y.students_per_employee,
            }))}
          />
          {exOnline.length ? (
            <div>
              <div className="mb-1 flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Excluding the online campus
                <Def def={def} k="growth_ex_online" />
              </div>
              <YearTable
                def={def}
                rows={exOnline.map((y) => ({
                  key: `${y.label}-ex-online`,
                  asAt: y.as_at,
                  employees: y.employees_ex_online,
                  students: y.students_ex_online,
                  perEmployee: y.students_per_employee_ex_online,
                }))}
              />
            </div>
          ) : null}
        </div>
        <div>
          <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              Index, {fmtMonth(first.as_at)} = 100
            </span>
            <LegendSwatch color={EMPLOYEES_COLOR} label="Employees in post" defKey="growth_employees" def={def} />
            <LegendSwatch color={STUDENTS_COLOR} label="Invoiced students" defKey="growth_students" def={def} />
            <LegendSwatch color={TITLES_COLOR} label="Role titles" defKey="growth_role_titles" def={def} />
          </div>
          {chart.length > 1 ? (
            <div style={{ width: '100%', height: 180 }}>
              <ResponsiveContainer>
                <LineChart data={chart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: AXIS }}
                  />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} domain={['auto', 'auto']} />
                  <RechartsTooltip
                    labelStyle={{ fontWeight: 600 }}
                    contentStyle={{ fontSize: 12 }}
                    formatter={(value: number | string, name: string) => [
                      typeof value === 'number' ? value.toFixed(1) : 'not available',
                      name,
                    ]}
                  />
                  <Line
                    dataKey="employees"
                    name="Employees in post"
                    stroke={EMPLOYEES_COLOR}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Line
                    dataKey="students"
                    name="Invoiced students"
                    stroke={STUDENTS_COLOR}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Line
                    dataKey="role_titles"
                    name="Role titles"
                    stroke={TITLES_COLOR}
                    strokeWidth={2}
                    strokeDasharray="4 3"
                    dot={{ r: 3 }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : null}
          {movements.length ? (
            <div className="mt-1 text-[11px] leading-snug text-gray-600">
              From {fmtDay(first.as_at)} to {fmtDay(last.as_at)}: {movements.join(', ')}.
            </div>
          ) : null}
        </div>
      </div>
      <div className="mt-2 space-y-1 text-[11px] leading-snug text-gray-500">
        <div>
          Students and employees are both taken at {on} of each year.
          {onlineRemoved
            ? firstHadNoOnlineStudents
              ? ` The online campus had no students at the first ${on}, so it is removed from both sides for the like-for-like view.`
              : ' The online campus is removed from both sides for the like-for-like view.'
            : ''}
          {retiredCaveat
            ? ' Staff in positions since retired carry no campus in the current records, so the earlier years excluding online may still include some online staff.'
            : ''}
        </div>
        {bridge && isNum(bridgePublished) && isNum(bridgeNow) && bridge.now_differs?.includes('growth_employees_pct') ? (
          <div>
            <b className="text-gray-600">Since the board pack.</b> The board pack’s {spanWords} growth of{' '}
            {fmtPct(bridgePublished)} for employees read the headcount of {fmtDay(bridge.read_on)} against{' '}
            {month ? `${month} ` : ''}students; on {on} of both years it is {fmtPct(bridgeNow)}.
          </div>
        ) : null}
      </div>
    </div>
  );
};

// =============================================================================
// PANEL
// =============================================================================

export const RatioPanel: React.FC<RatioPanelProps> = ({ panels, loading, def }) => {
  const d = useMemo<DefLookup>(() => (k: string) => def(k) ?? LOCAL_DEFS[k], [def]);
  const names = useMemo(() => readCampusNames(panels), [panels]);
  const ratio = panels?.ratio ?? null;
  const rows = ratio?.by_campus ?? [];
  const total = ratio?.total ?? null;
  const detail = readExtras(panels, 'ratio_detail');
  const anyWithheld = rows.some((r) => r.suppressed);

  const lecturerBridge = finalisedBridge(detail?.published, 'M34');
  const bridgeLecturers = lecturerBridge?.published?.lecturer_fte;
  const bridgeRatio = lecturerBridge?.published?.students_per_lecturer_fte;
  const wholeFte = detail?.reconciliation?.whole_department_fte;
  const wholeRatio = detail?.reconciliation?.ratio_whole_department;
  const noLecturer = Array.isArray(detail?.campuses_without_lecturer_fte)
    ? detail.campuses_without_lecturer_fte.filter(Boolean)
    : [];

  /** A withheld row's FTE and ratio are dashed, never worked back from the total. */
  const figure = (r: WorkforceRatioRow, v: number | null) =>
    r.suppressed ? (
      <Dash def={d} k="ratio_withheld" text="withheld to protect small groups" />
    ) : v === null ? (
      <Dash def={d} k="ratio_not_available" text="not available for this date" />
    ) : (
      fmtOne(v)
    );

  return (
    <PanelShell
      title="Students per lecturer FTE"
      defKey="students_per_lecturer_fte"
      def={d}
      window={
        rows.length
          ? `${panelDateLabel(panels, 'ratio')} · invoiced students (new and returning) ÷ lecturer full-time equivalents paid in the month. Independent of the basis toggle and the period picker.`
          : 'Invoiced students (new and returning) ÷ lecturer full-time equivalents paid in the month. Independent of the basis toggle and the period picker.'
      }
      /* The claim that used to sit here — that these figures are "derivable from
         F&O invoicing, which would let this panel refresh with the sync" — is not
         true and actively invites a wrong port: no invoicing entity is registered
         or reachable, and approximating the denominator from F&O lecturer seats
         runs 28.8% low nationally and moves Online from worst to mid-pack. */
      footnote={
        <>
          Student counts come from a Finance file and lecturer FTE from the payroll month. It is
          updated when Finance closes a payroll month. Both sides are month aggregates; it is not a
          point-in-time measure.
          {anyWithheld
            ? ' A campus paying fewer than five lecturer full-time equivalents has its FTE and ratio withheld to protect small groups; its students and lecturers stay in the total.'
            : ''}
        </>
      }
    >
      {loading && !panels ? (
        <PanelSkeleton height={220} />
      ) : !panels ? (
        <PanelUnavailable what="Students per lecturer FTE" />
      ) : !rows.length ? (
        <div className="rounded border border-gray-100 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-500">
          Students per lecturer FTE is not available for this date.
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="border-b text-gray-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium">
                      <span className="inline-flex items-center">
                        Campus
                        <Def def={d} k="campus" />
                      </span>
                    </th>
                    <th className="px-2 py-1.5 text-right font-medium">
                      <span className="inline-flex items-center justify-end">
                        Invoiced students
                        <Def def={d} k="students" />
                      </span>
                    </th>
                    <th className="px-2 py-1.5 text-right font-medium">
                      <span className="inline-flex items-center justify-end">
                        Lecturer FTE
                        <Def def={d} k="lecturer_fte" />
                      </span>
                    </th>
                    <th className="px-2 py-1.5 text-right font-medium">
                      <span className="inline-flex items-center justify-end">
                        Students per FTE
                        <Def def={d} k="ratio" />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.campus} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-2 py-1.5 text-gray-700">
                        {names[r.campus] ? (
                          <>
                            {names[r.campus]} <span className="text-gray-400">({r.campus})</span>
                          </>
                        ) : (
                          r.campus
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                        {r.students === null ? (
                          <Dash def={d} k="ratio_no_students" text="no student figure" />
                        ) : (
                          fmtNum(r.students)
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                        {figure(r, r.lecturer_fte)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-medium tabular-nums text-gray-800">
                        {figure(r, r.ratio)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {total ? (
                  <tfoot>
                    <tr className="border-t-2">
                      <td className="px-2 py-1.5 font-semibold text-heritage-blue">All campuses</td>
                      <td className="px-2 py-1.5 text-right font-bold tabular-nums text-heritage-blue">
                        {fmtNum(total.students)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-bold tabular-nums text-heritage-blue">
                        {fmtOne(total.lecturer_fte)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-bold tabular-nums text-heritage-blue">
                        {fmtOne(total.ratio)}
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
            <div className="mt-2 space-y-1 text-[11px] leading-snug text-gray-500">
              {!total ? <div>The all-campus total is not available for this date.</div> : null}
              {isNum(wholeFte) && isNum(wholeRatio) ? (
                <div>
                  On the whole academic department ({fmtOne(wholeFte)} FTE), the basis Finance quotes:{' '}
                  {fmtOne(wholeRatio)} students per FTE.
                </div>
              ) : null}
              {noLecturer.map((c) => (
                <div key={`no-lecturer-${c.campus}`}>
                  {fmtNum(c.students)} students at {names[c.campus] ?? c.campus} are invoiced with no
                  lecturer paid there; they are in the total.
                </div>
              ))}
              {lecturerBridge &&
              isNum(bridgeLecturers) &&
              isNum(bridgeRatio) &&
              lecturerBridge.now_differs?.length ? (
                <div>
                  <b className="text-gray-600">Since the board pack.</b> The board pack read lecturers on{' '}
                  {fmtDay(lecturerBridge.read_on)}: {fmtOne(bridgeLecturers)} lecturer FTE and{' '}
                  {fmtOne(bridgeRatio)} students per FTE. {lecturerBridge.now_cause}
                </div>
              ) : null}
            </div>
          </div>

          {detail?.growth ? (
            <GrowthSection
              growth={detail.growth}
              bridge={finalisedBridge(detail.published, 'M12')}
              payrollMonth={detail.payroll_month}
              def={d}
            />
          ) : null}
        </div>
      )}
    </PanelShell>
  );
};
